import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  IconButton,
  alpha,
  Divider,
  Avatar,
} from "@mui/material";
import {
  DashboardOutlined,
  Inventory2Outlined,
  PeopleOutlined,
  AccountBalanceWalletOutlined,
  HistoryOutlined,
  LocalShippingOutlined,
  LocationOnOutlined,
  ReceiptLongOutlined,
  RuleOutlined,
  ChevronLeft,
  ChevronRight,
  ViewInArOutlined,
  StorefrontOutlined,
  FlashOnOutlined,
  EventNoteOutlined,
  ShoppingCartOutlined,
  DescriptionOutlined,
  BusinessOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router";
import { usePermission } from "../contexts/PermissionContext";
import { useAuth } from "../contexts/AuthContext";
import { Permissions } from "../constants/permissions";
import companyService from "../services/companyService";

const FULL_DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 80;

export const Sidebar = ({
  mobileOpen,
  handleDrawerToggle,
  isCollapsed,
  setIsCollapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, globalSettings } = useAuth();
  const { hasPermission } = usePermission();
  const [name, setName] = useState(user?.name || "Corporate Account");
  const [companyLogo, setCompanyLogo] = useState(user?.companyLogo || null);

  useEffect(() => {
    if ((!user?.name || !companyLogo) && user?.companyId) {
      companyService
        .getProfile()
        .then((res) => {
          if (res?.name) {
            setName(res.name);
          }
          if (res?.logoUrl) {
            setCompanyLogo(res.logoUrl);
          }
        })
        .catch((err) =>
          console.error("Failed to fetch sidebar company data", err),
        );
    }
  }, [user]);

  const menuGroups = [
    {
      title: "Overview",
      items: [
        {
          text: "Dashboard",
          icon: <DashboardOutlined />,
          path: "/dashboard",
          permission: Permissions.B2B_DASHBOARD_VIEW,
        },
      ],
    },
    {
      title: "Sourcing",
      items: [
        {
          text: "In-Stock Catalog",
          icon: <Inventory2Outlined />,
          path: "/catalog",
          permission: Permissions.PRODUCT_READ,
          visible: company?.isReadyStockCatalogEnabled ?? true,
        },
        {
          text: "Pre-Orders Catalog",
          icon: <EventNoteOutlined />,
          path: "/pre-order-items",
          permission: Permissions.PRODUCT_READ,
          visible:
            (company?.isPreOrderCatalogEnabled ?? true) &&
            globalSettings?.isActive !== false,
        },
        {
          text: "Distributors",
          icon: <StorefrontOutlined />,
          path: "/suppliers",
          permission: Permissions.SUPPLIER_READ,
        },
        {
          text: "Quick Order",
          icon: <FlashOnOutlined />,
          path: "/quick-order",
          permission: Permissions.B2B_ORDER_MANAGE,
          visible: company?.isBulkOrdersEnabled ?? false,
        },
      ],
    },
    {
      title: "Purchasing",
      items: [
        {
          text: "Cart & Checkout",
          icon: <ShoppingCartOutlined />,
          path: "/checkout",
          permission: Permissions.B2B_ORDER_MANAGE,
        },
        {
          text: "Orders History",
          icon: <LocalShippingOutlined />,
          path: "/orders",
          permission: Permissions.B2B_ORDER_READ,
        },
        {
          text: "Pre-Orders History",
          icon: <EventNoteOutlined />,
          path: "/pre-orders",
          permission: Permissions.B2B_ORDER_READ,
        },
        {
          text: "Quotations History",
          icon: <DescriptionOutlined />,
          path: "/quotes",
          permission: Permissions.B2B_QUOTE_READ,
          visible: company?.isQuotationEnabled ?? false,
        },
        {
          text: "Returns & Claims",
          icon: <HistoryOutlined />,
          path: "/rma",
          permission: Permissions.B2B_ORDER_READ,
          visible: company?.isRMAEnabled ?? false,
        },
        {
          text: "Consignment",
          icon: <StorefrontOutlined />,
          path: "/consignment",
          permission: Permissions.B2B_CONSIGNMENT_READ,
        },
      ],
    },
    {
      title: "Finance",
      items: [
        {
          text: "Finance Hub",
          icon: <ReceiptLongOutlined />,
          path: "/finance",
          permission: Permissions.B2B_FINANCE_READ,
        },
        {
          text: "Ledger",
          icon: <AccountBalanceWalletOutlined />,
          path: "/ledger",
          permission: Permissions.B2B_FINANCE_READ,
        },
      ],
    },
    {
      title: "Administration",
      items: [
        {
          text: "Team Management",
          icon: <PeopleOutlined />,
          path: "/team",
          permission: Permissions.B2B_USER_MANAGE,
          visible: company?.isTeamManagementEnabled ?? false,
        },
        {
          text: "Addresses Book",
          icon: <LocationOnOutlined />,
          path: "/addresses",
          permission: Permissions.B2B_COMPANY_UPDATE,
        },
        {
          text: "Approval Workflows",
          icon: <RuleOutlined />,
          path: "/approval-settings",
          permission: Permissions.B2B_COMPANY_UPDATE,
          visible: company?.isApprovalWorkflows ?? false,
        },
        {
          text: "Audit Logs",
          icon: <HistoryOutlined />,
          path: "/activity",
          permission: Permissions.B2B_COMPANY_READ,
        },
      ],
    },
  ];

  const renderDrawerContent = (forceExpand = false) => {
    const collapsed = forceExpand ? false : isCollapsed;
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minHeight: 64,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <ViewInArOutlined />
          </Box>
          {!collapsed && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                letterSpacing: "-0.02em",
              }}
            >
              Jigenpass
            </Typography>
          )}
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 1 }}>
          {menuGroups.map((group, index) => {
            const filteredItems = group.items.filter((item) => {
              if (item.visible === false) return false;
              if (!item.permission) return true;
              return hasPermission(item.permission, {
                logic: item.logic || "AND",
              });
            });

            if (filteredItems.length === 0) return null;

            return (
              <Box key={group.title} sx={{ mb: 1.5 }}>
                {!collapsed && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.5,
                      py: 1,
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {group.title}
                  </Typography>
                )}
                <List disablePadding>
                  {filteredItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                        <Tooltip
                          title={collapsed ? item.text : ""}
                          placement="right"
                        >
                          <ListItemButton
                            onClick={() => {
                              navigate(item.path);
                              if (mobileOpen) handleDrawerToggle();
                            }}
                            sx={{
                              minHeight: 40,
                              borderRadius: 1.5,
                              px: collapsed ? 0 : 1.5,
                              justifyContent: collapsed
                                ? "center"
                                : "flex-start",
                              bgcolor: isActive
                                ? alpha("#2563eb", 0.08)
                                : "transparent",
                              color: isActive
                                ? "primary.main"
                                : "text.secondary",
                              "&:hover": {
                                bgcolor: alpha("#2563eb", 0.04),
                                color: "primary.main",
                                "& .MuiListItemIcon-root": {
                                  color: "primary.main",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: collapsed ? 0 : 1.5,
                                justifyContent: "center",
                                color: isActive
                                  ? "primary.main"
                                  : "text.secondary",
                              }}
                            >
                              {React.cloneElement(item.icon)}
                            </ListItemIcon>
                            {!collapsed && (
                              <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                  fontWeight: isActive ? 600 : 500,
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
                {index < menuGroups.length - 1 && (
                  <Divider sx={{ my: 1, mx: 1.5, opacity: 0.5 }} />
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              p: collapsed ? 1 : 2,
              borderRadius: 2,
              bgcolor: collapsed ? "transparent" : "background.default",
              border: collapsed ? "none" : `1px solid`,
              borderColor: "divider",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              src={companyLogo}
              variant="rounded"
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha("#2563eb", 0.1),
                color: "primary.main",
                borderRadius: 1.5,
                border: companyLogo ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <BusinessOutlined sx={{ fontSize: 20 }} />
            </Avatar>
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  COMPANY
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {name}
                </Typography>
              </Box>
            )}
          </Box>
          {!forceExpand && (
            <Box
              sx={{
                display: "flex",
                justifyContent: collapsed ? "center" : "flex-end",
              }}
            >
              <IconButton
                onClick={() => setIsCollapsed(!isCollapsed)}
                size="small"
                sx={{
                  border: `1px solid`,
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                {collapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const currentWidth = isCollapsed ? COLLAPSED_DRAWER_WIDTH : FULL_DRAWER_WIDTH;

  return (
    <Box
      component="nav"
      sx={{
        width: { md: currentWidth },
        flexShrink: { md: 0 },
        transition: "width 0.2s",
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: FULL_DRAWER_WIDTH,
            border: "none",
          },
        }}
      >
        {renderDrawerContent(true)}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: currentWidth,
            overflowX: "hidden",
          },
        }}
        open
      >
        {renderDrawerContent(false)}
      </Drawer>
    </Box>
  );
};
